<template>
  <BarChartGenerator :chart-data="chartData" :chart-options="computedChartOptions" :plugins="[ChartDataLabels]" />
</template>

<script setup>
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import merge from 'lodash/merge';
import { computed } from 'vue';
import { Bar as BarChartGenerator } from 'vue-chartjs';
import { PaletteColors } from '@/Theme';
import { toCurrency } from '@/utils';
import {
  DEFAULT_TOOLTIP_STYLES,
  generateGradientBackground,
  getDefaultGridStyles,
  shouldShowDataLabel,
} from '@/utils/chart';
import { hexToRGB, isValidHex } from '@/utils/color';

ChartJS.register(CategoryScale, Legend, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip);

const props = defineProps({
  labels: {
    type: Object,
    required: true,
  },
  barDataset: {
    type: Object,
    required: true,
  },
  lineDataset: {
    type: Object,
    required: true,
  },
  chartOptions: Object,
  fillGradient: Boolean,
});

const getColor = color => (isValidHex(color) ? color : PaletteColors['brand-primary']);

const chartData = computed(() => ({
  labels: props.labels.values,
  datasets: [
    {
      type: 'bar',
      label: props.barDataset.name,
      data: props.barDataset.values,
      backgroundColor: hexToRGB(getColor(props.barDataset.barColor), 0.5),
      borderColor: getColor(props.barDataset.barColor),
      borderRadius: 4,
      borderWidth: 1,
      maxBarThickness: 35,
      yAxisID: 'y',
      order: 2,
      datalabels: { display: false },
    },
    {
      type: 'line',
      label: props.lineDataset.name,
      data: props.lineDataset.values,
      borderColor: getColor(props.lineDataset.lineColor),
      backgroundColor: props.fillGradient
        ? ctx => generateGradientBackground(ctx, getColor(props.lineDataset.lineColor))
        : getColor(props.lineDataset.lineColor),
      fill: props.fillGradient,
      tension: 0.3,
      pointBackgroundColor: PaletteColors['base-white'],
      pointBorderColor: getColor(props.lineDataset.lineColor),
      pointBorderWidth: 2,
      pointRadius: 3,
      yAxisID: 'y1',
      order: 1,
      datalabels: {
        anchor: 'end',
        align: 'top',
        font: { size: 10 },
        color: getColor(props.lineDataset.lineColor),
        display: shouldShowDataLabel,
        formatter: value => toCurrency(value * 100),
      },
    },
  ],
}));

const computedChartOptions = computed(() => {
  const defaultGridStyles = getDefaultGridStyles();

  return merge(
    {
      aspectRatio: 2,
      maintainAspectRatio: true,
      responsive: true,
      layout: { padding: { top: 20 } },
      interaction: {
        mode: 'index',
        intersect: false,
      },
      scales: {
        x: merge({}, defaultGridStyles),
        y: merge({}, defaultGridStyles, {
          type: 'linear',
          display: true,
          position: 'left',
          title: {
            display: !!props.barDataset.name,
            text: props.barDataset.name,
          },
          ticks: {
            callback: label => (!isNaN(label) ? toCurrency(label * 100) : label),
          },
        }),
        y1: merge({}, defaultGridStyles, {
          type: 'linear',
          display: true,
          position: 'right',
          grace: '10%',
          title: {
            display: !!props.lineDataset.name,
            text: props.lineDataset.name,
          },
          grid: {
            drawOnChartArea: false,
          },
          ticks: {
            callback: label => (!isNaN(label) ? toCurrency(label * 100) : label),
          },
        }),
      },
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            color: PaletteColors['default-lighten-30'],
            usePointStyle: true,
            padding: 20,
          },
        },
        tooltip: merge({}, DEFAULT_TOOLTIP_STYLES, {
          callbacks: {
            label: context => {
              const label = context.dataset.label ? `${context.dataset.label}: ` : '';
              return `${label}${!isNaN(context.parsed.y) ? toCurrency(context.parsed.y * 100) : ''}`;
            },
          },
        }),
      },
    },
    props.chartOptions,
  );
});
</script>
